import { computed, nextTick, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useToasts } from '@/app/toasts'
import {
  classifyCreateAccountError,
  type CreateAccountError,
  type CreateAccountInput,
  type UpdateAccountInput,
} from '../internal/account-form.ts'
import type { UseAccountsTree } from './use-accounts-tree.ts'

/** Create and edit dialogs for one ledger's chart of accounts, shared by every page that offers them. */
export function useAccountMaintenance(tree: UseAccountsTree) {
  const { t } = useI18n()
  const { push: pushToast } = useToasts()

  const createOpen = ref(false)
  const createParentId = ref<string | undefined>(undefined)
  const createError = ref<CreateAccountError | undefined>(undefined)

  function openCreate(parentId?: string): void {
    createParentId.value = parentId
    createError.value = undefined
    createOpen.value = true
  }

  async function submitCreate(input: CreateAccountInput): Promise<void> {
    createError.value = undefined
    try {
      const id = await tree.create(input)
      createOpen.value = false
      if (input.isGroup) {
        await nextTick()
        openCreate(id)
      }
    } catch (error) {
      createError.value = classifyCreateAccountError(error)
    }
  }

  const editOpen = ref(false)
  const editRowId = ref<string | undefined>(undefined)
  const editRow = computed(() => tree.allRows.value.find((row) => row.id === editRowId.value))
  const updateHasError = ref(false)

  function openEdit(id: string): void {
    editRowId.value = id
    updateHasError.value = false
    editOpen.value = true
  }

  async function submitUpdate(input: UpdateAccountInput): Promise<void> {
    try {
      await tree.update(input)
      editOpen.value = false
    } catch {
      updateHasError.value = true
    }
  }

  const archiveRowId = ref<string | undefined>(undefined)
  const archiveRow = computed(() => tree.allRows.value.find((row) => row.id === archiveRowId.value))
  const archiveHasError = ref(false)

  function requestArchive(id: string): void {
    archiveRowId.value = id
    archiveHasError.value = false
  }

  async function submitArchive(): Promise<void> {
    const id = archiveRowId.value
    if (id === undefined) return
    archiveHasError.value = false
    try {
      await tree.archive(id)
      archiveRowId.value = undefined
    } catch {
      archiveHasError.value = true
    }
  }

  async function restore(id: string): Promise<void> {
    try {
      await tree.restore(id)
    } catch {
      pushToast('danger', t('accounts.restoreFailed'))
    }
  }

  return {
    createOpen,
    createParentId,
    createError,
    openCreate,
    submitCreate,
    editOpen,
    editRow,
    updateHasError,
    openEdit,
    submitUpdate,
    archiveRow,
    archiveHasError,
    isArchiving: tree.isArchiving,
    requestArchive,
    submitArchive,
    restore,
    closeArchive: () => (archiveRowId.value = undefined),
    closeCreate: () => (createOpen.value = false),
    closeEdit: () => (editOpen.value = false),
  }
}
